import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./HowItWorks.css";

import I18nText from "../components/I18nText";
import { getFrames } from "../config/howItWorksFrames";
import {
    HOW_IT_WORKS_FRAME_DURATION_MS,
    HOW_IT_WORKS_INITIAL_DELAY_MS,
    SIMULATOR_HANDOFF_ON_HOVER,
    YEAR_10_CASH_VALUE,
} from "../config/constants";
import { track } from "../lib/analytics";

const SESSION_KEY = "laguna_how_autoplayed";
let autoplayDecidedThisLoad = false;

function Panel({ role, data, displayed, roleLabel }) {
    const isDimmed = !!data.dimmed;
    return (
        <div
            className={`panel ${role}${isDimmed ? " dimmed" : ""}`}
            data-testid={`panel-${role}`}
        >
            <div className="panel-label">{roleLabel}</div>
            <div className="panel-body">
                <h3
                    className="panel-headline"
                    data-testid={`${role}-headline`}
                    style={{ opacity: displayed ? 1 : 0 }}
                >
                    {data.headline}
                </h3>
                {isDimmed ? (
                    <p
                        className="panel-detail"
                        data-testid={`${role}-detail`}
                        style={{ opacity: displayed ? 1 : 0 }}
                    >
                        <span className="dimmed-caption">{data.detail}</span>
                    </p>
                ) : (
                    <p
                        className="panel-detail"
                        data-testid={`${role}-detail`}
                        style={{ opacity: displayed ? 1 : 0 }}
                    >
                        {data.detail}
                    </p>
                )}
                {Array.isArray(data.stats) && data.stats.length > 0 && (
                    <div
                        className="panel-stat"
                        data-testid={`${role}-stat`}
                        style={{ opacity: displayed ? 1 : 0 }}
                    >
                        {data.stats.map((row, i) => {
                            const [label, value, coral] = row;
                            return (
                                <div className="panel-stat-item" key={i}>
                                    <div className="panel-stat-label">{label}</div>
                                    <div
                                        className={`panel-stat-value${coral ? " coral" : ""}`}
                                    >
                                        {value}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function Simulator() {
    const { t } = useTranslation();
    const frames = useMemo(() => getFrames(t), [t]);
    const [current, setCurrent] = useState(0);
    const [status, setStatus] = useState("idle");
    const [displayed, setDisplayed] = useState(true);
    const timerRef = useRef(null);
    const autoPlayingRef = useRef(false);
    const currentRef = useRef(0);
    const panelsRef = useRef(null);
    const initialDelayRef = useRef(null);

    const clearTimers = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (initialDelayRef.current) {
            clearTimeout(initialDelayRef.current);
            initialDelayRef.current = null;
        }
    };

    const transitionTo = useCallback(
        (idx) => {
            const next = Math.max(0, Math.min(frames.length - 1, idx));
            setDisplayed(false);
            setTimeout(() => {
                currentRef.current = next;
                setCurrent(next);
                setDisplayed(true);
            }, 250);
        },
        [frames.length],
    );

    const stopAutoPlay = useCallback((finished) => {
        autoPlayingRef.current = false;
        clearTimers();
        if (finished) {
            setStatus("complete");
            track("simulator_autoplay_complete", {});
        } else {
            setStatus("handoff");
            track("simulator_user_takeover", {});
        }
    }, []);

    const handoff = useCallback(() => {
        if (autoPlayingRef.current) stopAutoPlay(false);
    }, [stopAutoPlay]);

    const startAutoPlay = useCallback(() => {
        autoPlayingRef.current = true;
        setStatus("playing");
        track("simulator_autoplay_start", {});
        initialDelayRef.current = setTimeout(() => {
            if (!autoPlayingRef.current) return;
            timerRef.current = setInterval(() => {
                if (!autoPlayingRef.current) return;
                const c = currentRef.current;
                if (c < frames.length - 1) {
                    transitionTo(c + 1);
                } else {
                    stopAutoPlay(true);
                }
            }, HOW_IT_WORKS_FRAME_DURATION_MS);
        }, HOW_IT_WORKS_INITIAL_DELAY_MS);
    }, [frames.length, stopAutoPlay, transitionTo]);

    useEffect(() => {
        if (autoplayDecidedThisLoad) return undefined;
        autoplayDecidedThisLoad = true;
        const already = window.sessionStorage.getItem(SESSION_KEY);
        if (already) {
            setStatus("handoff");
            return undefined;
        }
        window.sessionStorage.setItem(SESSION_KEY, "1");
        startAutoPlay();
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!SIMULATOR_HANDOFF_ON_HOVER) return undefined;
        const el = panelsRef.current;
        if (!el) return undefined;
        const onEnter = () => handoff();
        el.addEventListener("mouseenter", onEnter, { once: true });
        return () => el.removeEventListener("mouseenter", onEnter);
    }, [handoff]);

    const replay = () => {
        clearTimers();
        track("simulator_replay", {});
        setDisplayed(false);
        setTimeout(() => {
            currentRef.current = 0;
            setCurrent(0);
            setDisplayed(true);
            startAutoPlay();
        }, 250);
    };

    const pickStop = (idx) => {
        track("simulator_stop_clicked", { stop_index: idx });
        handoff();
        transitionTo(idx);
    };

    const progressPct =
        current === 0 ? 0 : (current / (frames.length - 1)) * 100;

    const statusText =
        status === "playing"
            ? t("howItWorks.sim.statusPlaying")
            : status === "complete"
              ? t("howItWorks.sim.statusComplete")
              : t("howItWorks.sim.statusHandoff");

    const showReplay = status === "complete" || status === "handoff";
    const frame = frames[current];
    if (!frame) return null;

    return (
        <section className="simulator" data-testid="simulator">
            <div className="sim-controls">
                <div
                    className={`sim-status mono${status === "playing" ? " playing" : ""}`}
                    data-testid="sim-status"
                >
                    {statusText}
                </div>
                <button
                    type="button"
                    className={`replay-btn${showReplay ? " visible" : ""}`}
                    onClick={replay}
                    data-testid="replay-btn"
                    aria-hidden={!showReplay}
                    tabIndex={showReplay ? 0 : -1}
                >
                    {t("howItWorks.sim.replay")}
                </button>
            </div>
            <div className="timeline-wrap">
                <div className="timeline-track">
                    <div
                        className="timeline-progress"
                        style={{ width: `${progressPct}%` }}
                        data-testid="timeline-progress"
                    />
                </div>
                <div className="timeline-stops" data-testid="timeline-stops">
                    {frames.map((f, i) => (
                        <button
                            key={f.label + i}
                            type="button"
                            className={`timeline-stop${i === current ? " active" : ""}${i < current ? " past" : ""}`}
                            onClick={() => pickStop(i)}
                            data-testid={`timeline-stop-${i}`}
                            aria-label={f.label}
                        >
                            <span className="timeline-stop-label">{f.label}</span>
                        </button>
                    ))}
                </div>
            </div>
            <div className="panels" ref={panelsRef} data-testid="panels">
                <Panel
                    role="employer"
                    data={frame.employer}
                    displayed={displayed}
                    roleLabel={t("howItWorks.sim.labelEmployer")}
                />
                <Panel
                    role="employee"
                    data={frame.employee}
                    displayed={displayed}
                    roleLabel={t("howItWorks.sim.labelEmployee")}
                />
            </div>
            <div className="sim-footnote">
                <strong>{t("howItWorks.sim.footnoteTag")}</strong>
                {" "}
                {t("howItWorks.sim.footnoteBody")}
            </div>
            <div className="sim-footnote" data-testid="sim-dual-horizon-footnote">
                {t("howItWorks.footnote.dualHorizon")}
            </div>
        </section>
    );
}

function Comparison() {
    const { t } = useTranslation();
    const fmt = (n) => "$" + n.toLocaleString();
    return (
        <section className="comparison" data-testid="comparison">
            <div className="comparison-inner">
                <div className="section-label mono">{t("howItWorks.comparison.label")}</div>
                <h2>
                    <I18nText i18nKey="howItWorks.comparison.h2" />
                </h2>
                <div className="comparison-grid">
                    <div className="comp-col worse">
                        <div className="comp-label mono">
                            {t("howItWorks.comparison.termLabel")}
                        </div>
                        <div className="comp-title">{t("howItWorks.comparison.termTitle")}</div>
                        <div className="comp-outcome">
                            <div className="comp-outcome-label mono">
                                {t("howItWorks.comparison.outcomeLabel")}
                            </div>
                            <div
                                className="comp-outcome-value"
                                data-testid="comp-term-outcome"
                            >
                                {t("howItWorks.comparison.termOutcome")}
                            </div>
                        </div>
                        <p className="comp-desc">{t("howItWorks.comparison.termBody")}</p>
                    </div>
                    <div className="comp-col better">
                        <div className="comp-label mono">
                            {t("howItWorks.comparison.usLabel")}
                        </div>
                        <div className="comp-title">{t("howItWorks.comparison.usTitle")}</div>
                        <div className="comp-outcome">
                            <div className="comp-outcome-label mono">
                                {t("howItWorks.comparison.outcomeLabel")}
                            </div>
                            <div
                                className="comp-outcome-value"
                                data-testid="comp-ten-year"
                            >
                                {fmt(YEAR_10_CASH_VALUE)}
                            </div>
                        </div>
                        <p className="comp-desc">{t("howItWorks.comparison.usBody")}</p>
                    </div>
                </div>
            </div>
        </section>
    );
}

function FaqLite() {
    const { t } = useTranslation();
    const items = t("howItWorks.faq.items", { returnObjects: true }) || [];
    return (
        <section className="faq" data-testid="faq">
            <div className="section-label mono">{t("howItWorks.faq.label")}</div>
            <h2>
                <I18nText i18nKey="howItWorks.faq.h2" />
            </h2>
            {items.map((item, i) => (
                <div className="faq-item" key={i} data-testid={`faq-item-${i}`}>
                    <div className="faq-q">{item.q}</div>
                    <div className="faq-a">
                        <p>
                            <strong>{item.aStrong}</strong> {item.a}
                        </p>
                    </div>
                </div>
            ))}
        </section>
    );
}

function FinalCTA() {
    const { t } = useTranslation();
    const onClick = (role) => () =>
        track("how_it_works_cta_click", { role });
    return (
        <section className="hiw-final-cta" data-testid="hiw-final-cta">
            <div
                className="section-label mono"
                style={{ justifyContent: "center" }}
            >
                {t("howItWorks.cta.label")}
            </div>
            <h2>
                <I18nText i18nKey="howItWorks.cta.h2" />
            </h2>
            <p className="sub">{t("howItWorks.cta.sub")}</p>
            <div className="dual-cta">
                <Link
                    to="/employers"
                    onClick={onClick("employer")}
                    data-testid="hiw-cta-employer"
                >
                    <div className="dual-cta-label mono">{t("howItWorks.cta.employerLabel")}</div>
                    <div className="dual-cta-title">{t("howItWorks.cta.employerTitle")}</div>
                    <div className="dual-cta-sub">{t("howItWorks.cta.employerSub")}</div>
                </Link>
                <Link
                    to="/employees"
                    onClick={onClick("employee")}
                    data-testid="hiw-cta-employee"
                >
                    <div className="dual-cta-label mono">{t("howItWorks.cta.employeeLabel")}</div>
                    <div className="dual-cta-title">{t("howItWorks.cta.employeeTitle")}</div>
                    <div className="dual-cta-sub">{t("howItWorks.cta.employeeSub")}</div>
                </Link>
            </div>
        </section>
    );
}

export default function HowItWorks() {
    const { t } = useTranslation();
    useEffect(() => {
        track("how_it_works_view", {});
        document.title = t("howItWorks.title");
    }, [t]);

    return (
        <div data-testid="how-it-works-page">
            <section className="hiw-hero" data-testid="hiw-hero">
                <div className="section-label mono">{t("howItWorks.sectionLabel")}</div>
                <h1>
                    <I18nText i18nKey="howItWorks.h1" />
                </h1>
                <p className="hiw-hero-sub">{t("howItWorks.sub")}</p>
            </section>
            <Simulator />
            <Comparison />
            <FaqLite />
            <FinalCTA />
        </div>
    );
}
